import { useEffect, useState, useLayoutEffect } from 'react';
import { observeResize } from '../utils/observe-resize.js';
import { onFontsReady } from './text.utils.js';

/**
 * Detect overflow on the referenced element by comparing its scroll vs client extents in both
 * axes. Works for any of our truncation modes: `text-overflow: ellipsis` overflows horizontally,
 * `-webkit-line-clamp` overflows vertically, and the inactive axis is always equal.
 *
 * Purpose-built for the Text component, where the ref points at the element that owns the
 * clipping style — so we can skip the descendant tracking that the generic
 * `useDetectTruncated` hook needs.
 *
 * Watches two signals because either can flip the overflow state without the other firing:
 * `ResizeObserver` for the element's box, `MutationObserver` for descendant text or DOM changes
 * (the box can stay fixed-width while the content grows, e.g. a controlled input piping into
 * `children`).
 */
function useIsTruncated(ref) {
    const [isTruncated, setIsTruncated] = useState(false);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const check = () => setIsTruncated(hasOverflow(el));
        // Synchronous initial check — `ResizeObserver.observe()` queues its first entry for the
        // next render opportunity (not this commit), which paints once with the wrong value.
        // Calling `check()` here lets React flush the resulting state update before paint.
        check();
        const stopResize = observeResize(el, check);
        const mutationObserver = new MutationObserver(check);
        mutationObserver.observe(el, { characterData: true, childList: true, subtree: true });
        const stopFontsReady = onFontsReady(check);
        return () => {
            stopFontsReady();
            stopResize();
            mutationObserver.disconnect();
        };
    }, [ref]);
    return isTruncated;
}
function useOnTruncationChange(isTruncated, onTruncationChange) {
    useEffect(() => {
        onTruncationChange?.(isTruncated);
    }, [isTruncated, onTruncationChange]);
}
/**
 * Resolve `data-tooltip-*` props and a fallback pointerEnter handler. String children populate
 * the tooltip eagerly; for richer subtrees we read `textContent` lazily on hover so the tooltip
 * surface stays accurate when descendants change.
 */
function useTooltip(isTruncated, hideTooltip, children, onPointerEnterIn) {
    const isStringChild = typeof children === 'string';
    const initialText = isStringChild ? children : null;
    const [resolvedText, setResolvedText] = useState(initialText);
    useEffect(() => {
        // Clear when children transitions away from a string, otherwise the stale string lingers
        // as the tooltip until the pointerenter handler repopulates it from `textContent`.
        setResolvedText(isStringChild ? children : null);
    }, [children, isStringChild]);
    if (!isTruncated || hideTooltip) {
        return { tooltipProps: null, onPointerEnter: onPointerEnterIn };
    }
    const tooltipProps = resolvedText != null
        ? { 'data-tooltip': resolvedText, 'data-tooltip-type': 'text' }
        : null;
    const onPointerEnter = isStringChild
        ? onPointerEnterIn
        : (e) => {
            onPointerEnterIn?.(e);
            setResolvedText(e.currentTarget.textContent ?? '');
        };
    return { tooltipProps, onPointerEnter };
}
function hasOverflow(el) {
    return el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
}

export { useIsTruncated, useOnTruncationChange, useTooltip };
//# sourceMappingURL=text.hooks.js.map
